/* COLLECTION REVIEWS RAIL. YourBrandFuel, 2026-10-03.
   Loaded (defer) by sections/collection-reviews.liquid; styles in assets/collection-v2.css (.plp-rv).

   The section hands over the most reviewed products of the category. This script reads the review app's public
   feed (Okendo Storefront REST API, the one its own widgets call: no key, GET only) and draws the cards.
   Rules, so nobody has to read the code to know what is on the page:
   - reviews come back top-rated first (orderBy=rating desc, the old carousel's own rule); only 4 and 5 star
     reviews with real text are used (the section setting can raise that to 5)
   - order: long enough and not incentivized, then long enough and incentivized, then the short ones in the same
     two steps. A review the feed marks as incentivized always carries its label, on the card and in the sheet
   - "Verified buyer" is printed only where the feed says isVerified AND does not call the person something else
     (it marks a few as "Verified Reviewer"; the review app's own widget does not call those buyers either)
   - the products take turns, so one product does not fill the rail; a display name appears once per rail
   - every product handed over gets at least one card if it has a usable review at all: when the rail filled up
     without it, its best review takes the last place (from the product that had the most cards)
   - the customer's words are never edited: names and text only have their spacing tidied, the full text opens in
     the sheet. A customer's title that only repeats the product or scent name is left off the card (the strip
     under it says the same) and shown in the sheet
   - every number in the score is computed from the feed's own counts; the average is rounded DOWN to one decimal
     and the five-star share DOWN to a whole percent; the star levels are only shown when they add up to the count
   Review text is written with textContent, never as HTML. */
(() => {
  if (customElements.get('plp-reviews')) return;

  const API = 'https://api.okendo.io/v1/stores/';
  const CACHE_MS = 30 * 60 * 1000;
  const TIMEOUT_MS = 8000;
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  // the scents that have a colour dot in assets/collection-v2.css (.plp-chip__dot[data-swatch])
  const SWATCHES = ['royal-oud', 'stagecoach', 'hammer', 'lavender-patchouli', 'magic', 'gold', 'naked', 'vetiver-x', 'emerald-green-tea', 'the-gatsby', 'unscented', 'grove'];
  const AUTO_TITLE = /^\s*\d(\.\d)?\s*stars?\s*$/i;
  const SVG = 'http://www.w3.org/2000/svg';
  const calm = window.matchMedia('(prefers-reduced-motion: reduce)');

  const el = (tag, props, ...kids) => {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(props || {})) {
      if (value === false || value == null) continue;
      if (key === 'text') node.textContent = value;
      else if (key === 'style') node.style.cssText = value;
      else node.setAttribute(key, value === true ? '' : value);
    }
    node.append(...kids.flat().filter(Boolean));
    return node;
  };
  const fill = (node, ...kids) => node.replaceChildren(...kids.flat().filter(Boolean));

  const chevron = () => {
    const svg = document.createElementNS(SVG, 'svg');
    for (const [key, value] of Object.entries({ class: 'plp-rv__go', width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none', 'aria-hidden': 'true', focusable: 'false' })) svg.setAttribute(key, value);
    const path = document.createElementNS(SVG, 'path');
    path.setAttribute('d', 'm6 3 5 5-5 5');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.75');
    svg.append(path);
    return svg;
  };

  const tidy = (text) => String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
  const words = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(' ').filter(Boolean);
  const plural = (count, noun) => `${count.toLocaleString('en-US')} ${noun}${count === 1 ? '' : 's'}`;
  const nameKey = (card) => `name:${card.name.toLowerCase()}`;

  const when = (iso) => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? '' : `${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
  };

  // only https images from the hosts named; a missing address stays missing (it must not resolve to this page)
  const safeImage = (url, hosts) => {
    if (typeof url !== 'string' || !url.trim()) return '';
    try {
      const parsed = new URL(url, window.location.href);
      return parsed.protocol === 'https:' && hosts.some((host) => parsed.hostname === host || parsed.hostname.endsWith(`.${host}`)) ? parsed.href : '';
    } catch (error) {
      return '';
    }
  };

  const clean = (review, product, productIndex) => {
    const raw = String(review.body || '').trim();
    const body = raw.replace(/\s+/g, ' ');
    const reviewer = review.reviewer && typeof review.reviewer === 'object' ? review.reviewer : {};
    const variant = tidy(review.productVariantName);
    let fullTitle = tidy(review.title);
    if (AUTO_TITLE.test(fullTitle) || fullTitle.toLowerCase() === body.toLowerCase()) fullTitle = '';
    let title = fullTitle;
    if (title) {
      // a title that only repeats the product or the scent says nothing the product strip under it does not
      const known = new Set(words(`${product.name} ${review.productName || ''} ${variant} bossman brands`));
      const titleWords = words(title);
      if (titleWords.length && titleWords.every((word) => known.has(word))) title = '';
    }
    const status = tidy(reviewer.verifiedStatus);
    const photo = (Array.isArray(review.media) ? review.media : []).find((media) => media && media.type === 'image');
    return {
      id: String(review.reviewId || ''),
      p: productIndex,
      rating: Number(review.rating),
      title,
      fullTitle,
      body,
      full: raw.replace(/\n{3,}/g, '\n\n'),
      date: String(review.dateCreated || ''),
      name: tidy(reviewer.displayName),
      // strict the safe way round: the buyer label needs a plain yes, the incentive label shows on anything but a plain no
      verified: reviewer.isVerified === true && (!status || /^verified buyer$/i.test(status)),
      flagged: review.isIncentivized != null && review.isIncentivized !== false,
      variant: /^default title$/i.test(variant) ? '' : variant,
      image: safeImage(review.productImageUrl, ['cdn.shopify.com']),
      face: photo ? safeImage(photo.thumbnailUrl, ['okendo.io']) : '',
      photo: photo ? safeImage(photo.largeUrl || photo.largePortraitThumbnailUrl, ['okendo.io']) : '',
    };
  };

  const pick = (lists, { max, minRating, minChars }) => {
    const pools = lists.map((list) => list.filter((card) => card.id && card.name && Number.isFinite(card.rating)
      && card.rating >= minRating && card.rating <= 5 && card.body.length >= 20));
    const tiers = [
      (card) => !card.flagged && card.body.length >= minChars,
      (card) => card.flagged && card.body.length >= minChars,
      (card) => !card.flagged && card.body.length < minChars,
      (card) => card.flagged && card.body.length < minChars,
    ];
    const out = [];
    const used = new Set();
    const free = (card) => !used.has(card.id) && !used.has(nameKey(card));
    const take = (card) => { out.push(card); used.add(card.id); used.add(nameKey(card)); };

    // tier by tier, the products taking turns inside each tier
    for (const tier of tiers) {
      const queues = pools.map((pool) => pool.filter(tier));
      let moved = true;
      while (out.length < max && moved) {
        moved = false;
        for (const queue of queues) {
          if (out.length >= max) break;
          const index = queue.findIndex(free);
          if (index === -1) { queue.length = 0; continue; }
          take(queue[index]);
          queue.splice(0, index + 1);
          moved = true;
        }
      }
    }

    // a product that got no card (its top reviews are all incentivized, say) takes the last place with its best one.
    // Repeated until nothing changes: a name freed by one swap can be the one another product was waiting for.
    for (let pass = 0, changed = true; changed && pass < pools.length; pass++) {
      changed = false;
      pools.forEach((pool, productIndex) => {
        if (out.some((card) => card.p === productIndex)) return;
        const best = tiers.map((tier) => pool.find((card) => tier(card) && free(card))).find(Boolean);
        if (!best) return;
        if (out.length >= max) {
          const counts = {};
          out.forEach((card) => { counts[card.p] = (counts[card.p] || 0) + 1; });
          const most = Number(Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0]);
          if (!(counts[most] >= 2)) return;
          const [gone] = out.splice(out.map((card) => card.p).lastIndexOf(most), 1);
          used.delete(gone.id);
          used.delete(nameKey(gone));
        }
        take(best);
        changed = true;
      });
    }
    return out;
  };

  const summarise = (data) => {
    const aggregate = data && data.reviewAggregate;
    const count = aggregate ? Number(aggregate.reviewCount) : 0;
    const total = aggregate ? Number(aggregate.reviewRatingValuesTotal) : 0;
    // a score that cannot be true is not shown: a whole number of reviews, an average from 1 to 5
    if (!Number.isInteger(count) || count < 1 || !Number.isFinite(total) || total / count < 1 || total / count > 5) return null;
    const byLevel = aggregate.reviewCountByLevel && typeof aggregate.reviewCountByLevel === 'object' ? aggregate.reviewCountByLevel : {};
    const levels = [5, 4, 3, 2, 1].map((level) => Number(byLevel[`level${level}Count`]) || 0);
    // the star levels are only used when each is a whole number and together they account for every review
    const sound = levels.every((value) => Number.isInteger(value) && value >= 0) && levels.reduce((sum, value) => sum + value, 0) === count;
    return { count, average: total / count, levels: sound ? levels : null };
  };

  const get = async (url, options) => {
    const control = new AbortController();
    const timer = setTimeout(() => control.abort(), TIMEOUT_MS);
    try {
      const response = await fetch(url, { signal: control.signal, credentials: 'omit', ...options });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await (options && options.text ? response.text() : response.json());
    } finally {
      clearTimeout(timer);
    }
  };

  const readCache = (key) => {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(key));
      return stored && Date.now() - stored.at < CACHE_MS && Array.isArray(stored.cards) ? stored : null;
    } catch (error) {
      return null;
    }
  };
  const writeCache = (key, data) => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify({ ...data, at: Date.now() }));
    } catch (error) { /* private mode or full: the next page asks the feed again */ }
  };

  // most reviewed first; a tie goes to the larger id (what the section's own cut does), never to the page's sort order
  const asProducts = (list) => (Array.isArray(list) ? list : []).filter((product) => product && product.id)
    .sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0) || Number(b.id) - Number(a.id));
  const sameOrigin = (url) => { try { return new URL(url, window.location.href).origin === window.location.origin; } catch (error) { return false; } };

  class PlpReviews extends HTMLElement {
    connectedCallback() {
      if (this.started) {
        // moved in the page (the theme editor reorders sections): watch the rail's width again
        if (this.resizer) this.resizer.observe(this.$('rail'));
        return;
      }
      this.started = true;

      try {
        this.products = asProducts(JSON.parse(this.$('products').textContent));
      } catch (error) {
        this.products = [];
      }
      this.options = {
        max: Number(this.dataset.max) || 9,
        minRating: Number(this.dataset.minRating) || 4,
        minChars: Number(this.dataset.minChars) || 60,
      };
      this.nameHeading();

      // nothing is asked of the feed until the section is close to the screen
      if ('IntersectionObserver' in window) {
        const watcher = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          watcher.disconnect();
          this.load();
        }, { rootMargin: '900px 0px' });
        watcher.observe(this);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      if (this.resizer) this.resizer.disconnect();
    }

    $(name) { return this.querySelector(`[data-rv="${name}"]`); }

    // "Beard Care reviews": only for the categories named in the theme editor (the page's H1 carries that name);
    // any other collection keeps the plain heading
    nameHeading() {
      const pattern = this.dataset.heading || '';
      const h1 = document.querySelector('.plp-head__title[data-plp-category]');
      const name = h1 ? tidy(h1.dataset.plpCategory) : '';
      if (!name || name.length > 30 || !pattern.includes('[category]')) return;
      const text = pattern.replace('[category]', name);
      this.$('heading').textContent = text;
      this.$('rail').setAttribute('aria-label', text);
    }

    // The page could not see the whole category (more products than one grid page, a filter, page 2 and later):
    // the list comes from sections/collection-reviews-list.liquid, rendered on its own for the plain category
    // (Shopify's section rendering: no filters, no page, nothing else of the page involved).
    async listProducts() {
      const here = new URL(window.location.href);
      // the category's own address (Liquid's collection.url: on vendor and type pages it carries q), not the
      // page's path, which can hold a tag (/collections/all-products/apparel) and would narrow the list
      const url = new URL(this.dataset.listUrl || here.pathname, here.origin);
      if (url.origin !== here.origin) throw new Error('list address');
      if (!this.dataset.listUrl && here.searchParams.has('q')) url.searchParams.set('q', here.searchParams.get('q'));
      if (here.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', here.searchParams.get('preview_theme_id'));
      url.searchParams.set('section_id', this.dataset.list);
      const html = await get(url, { credentials: 'same-origin', text: true });
      const list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-rv="products"]');
      // an answer with no list in it (a holding page, say) is a failed read, not "no reviewed products"
      if (!list) throw new Error('no list');
      const hidden = (this.dataset.hiddenTypes || '').toLowerCase();
      const max = Number(this.dataset.productsMax) || 4;
      return asProducts(JSON.parse(list.textContent))
        .filter((product) => !product.type || !hidden.includes(`,${String(product.type).toLowerCase().trim()},`))
        .filter((product) => sameOrigin(product.url))
        .slice(0, max);
    }

    async load() {
      if (this.dataset.list) {
        try {
          this.products = await this.listProducts();
        } catch (error) {
          // the list could not be read: use what this page had, or say so if it had nothing
          if (!this.products.length) {
            const height = this.offsetHeight;
            const hadFocus = this.contains(document.activeElement);
            this.drawScore(null);
            this.fail(height, hadFocus);
            return;
          }
        }
      }
      if (!this.products.length || !this.dataset.store) {
        this.none(this.offsetHeight, this.contains(document.activeElement));
        return;
      }

      const { max, minRating, minChars } = this.options;
      const key = `plp-rv:2:${this.dataset.store}:${this.products.map((product) => product.id).join('.')}:${max}.${minRating}.${minChars}`;
      let data = readCache(key);
      if (!data) {
        const base = API + encodeURIComponent(this.dataset.store);
        let failed = 0;
        const [score, ...lists] = await Promise.all([
          get(`${base}/review_aggregate`).then(summarise).catch(() => null),
          ...this.products.map((product, index) => get(`${base}/products/shopify-${encodeURIComponent(product.id)}/reviews?limit=25&orderBy=rating%20desc`)
            .then((answer) => (Array.isArray(answer.reviews) ? answer.reviews : []).filter((review) => review && typeof review === 'object').map((review) => clean(review, product, index)))
            .catch(() => { failed += 1; return []; })),
        ]);
        data = { score, cards: pick(lists, this.options), down: failed > 0 };
        // only a complete answer is kept: a half-failed one must not stick for half an hour
        if (data.cards.length && score && failed === 0) writeCache(key, data);
      }
      if (!this.isConnected) return;

      // taken before anything is redrawn: the fail state fills this height, and gets the focus back if it was here
      const height = this.offsetHeight;
      const hadFocus = this.contains(document.activeElement);
      this.drawScore(data.score);
      if (data.cards.length) this.drawCards(data.cards);
      else if (data.down) this.fail(height, hadFocus);
      else this.none(height, hadFocus);
    }

    stars(rating) {
      return el('span', { class: 'plp-rv__stars', role: 'img', 'aria-label': `${rating} out of 5 stars`, style: `--fill: ${(rating / 5) * 100}%` });
    }

    drawScore(score) {
      const box = this.$('score');
      if (!score) {
        // the score comes from the same feed: if that did not answer, no number is shown at all
        box.hidden = true;
        this.dataset.score = 'none';
        return;
      }
      const shown = (Math.floor(score.average * 10) / 10).toFixed(1);
      this.$('avg').textContent = shown;
      const stars = this.$('stars');
      stars.style.setProperty('--fill', `${(score.average / 5) * 100}%`);
      stars.setAttribute('aria-label', `${shown} out of 5 stars`);

      // the score is the whole store's and sits under a category heading: it never goes without its qualifier
      const scope = tidy(this.dataset.scope) || 'across the store';
      fill(this.$('count'), document.createTextNode(plural(score.count, 'review')), el('span', { class: 'plp-rv__scope', text: ` ${scope}` }));

      if (score.levels) {
        this.$('five').textContent = `${Math.floor((score.levels[0] * 100) / score.count)}% are five stars`;
        [...this.$('levels').children].forEach((row, index) => {
          const count = score.levels[index];
          const bar = row.querySelector('i');
          bar.style.width = `${(count / score.count) * 100}%`;
          bar.classList.toggle('is-some', count > 0);
          fill(row.querySelector('.plp-rv__level-count'), document.createTextNode(count.toLocaleString('en-US')), el('span', { class: 'sr-only', text: ' reviews' }));
        });
      } else {
        this.$('five').hidden = true;
        this.$('levels').hidden = true;
      }

      const label = (this.dataset.all || '').replace('[count]', score.count.toLocaleString('en-US'));
      if (label) this.querySelectorAll('[data-rv="all"]').forEach((link) => { link.textContent = label; });
      this.dataset.score = 'ready';
    }

    product(card, asLink) {
      const product = this.products[card.p];
      const parts = card.variant ? card.variant.split(' / ').map((part) => part.trim()).filter(Boolean) : [];
      const swatch = parts.length ? parts[0].toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') : '';
      const image = card.image || product.image;
      const kids = [
        image ? el('img', { class: 'plp-rv__thumb', src: image, alt: '', width: 56, height: 56, loading: 'lazy' }) : el('span', { class: 'plp-rv__thumb' }),
        el('span', { class: 'plp-rv__prodtext' },
          el('span', { class: 'plp-rv__pname', text: product.name }),
          parts.length ? el('span', { class: 'plp-rv__variant' },
            SWATCHES.includes(swatch) ? el('span', { class: 'plp-chip__dot', 'data-swatch': swatch }) : null,
            el('span', { text: parts.join(' · ') })) : null),
      ];
      if (!asLink) return kids;
      return el('a', { class: 'plp-rv__prod', href: product.url, 'aria-label': `${product.name}${parts.length ? `, ${parts.join(', ')}` : ''}. View product` }, kids, chevron());
    }

    card(card, index) {
      const short = !card.title && card.body.length <= 130;
      return el('li', { class: `plp-rv__card${short ? ' plp-rv__card--short' : ''}${card.flagged ? ' plp-rv__card--flagged' : ''}`, 'data-i': index },
        el('div', { class: 'plp-rv__body' },
          el('div', { class: 'plp-rv__top' },
            this.stars(card.rating),
            el('span', { class: 'plp-rv__date', text: when(card.date) }),
            card.verified && this.dataset.verified ? el('span', { class: 'plp-rv__verified', text: this.dataset.verified }) : null),
          el('div', { class: 'plp-rv__quote' },
            card.title ? el('h3', { class: 'plp-rv__title', text: card.title }) : null,
            el('p', { class: 'plp-rv__text', text: card.body })),
          el('div', { class: 'plp-rv__by' },
            card.face ? el('img', { class: 'plp-rv__face', src: card.face, alt: '', width: 28, height: 28, loading: 'lazy' }) : null,
            el('b', { class: 'plp-rv__who', text: card.name }),
            el('button', { type: 'button', class: 'plp-rv__more', 'data-open': true, 'aria-haspopup': 'dialog', hidden: true },
              el('span', { text: 'Read more' }),
              el('span', { class: 'sr-only', text: `, review by ${card.name}` }))),
          card.flagged ? el('p', { class: 'plp-rv__flag', text: this.dataset.flag || 'Incentivized review' }) : null),
        this.product(card, true));
    }

    drawCards(cards) {
      const rail = this.$('rail');
      this.cards = cards;
      fill(rail, cards.map((card, index) => this.card(card, index)));
      this.items = [...rail.children];
      rail.dataset.n = cards.length;
      rail.setAttribute('aria-busy', 'false');
      this.dataset.state = 'ready';

      const note = this.$('note');
      note.textContent = `${cards.length} top-rated shown`;
      note.classList.remove('is-pending');

      let frame = 0;
      rail.addEventListener('scroll', () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(() => this.sync()); }, { passive: true });
      rail.addEventListener('keydown', (event) => {
        if (event.target !== rail || (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft')) return;
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1, false);
      });
      // keyboard focus on a card that is half off the screen brings the whole card in (a tap or click does not move
      // it; a sheet closed with Escape hands focus back to its button and counts as keyboard focus). To the card's
      // start, which is a snap position: "nearest" was undone by the snap under 1000px.
      rail.addEventListener('focusin', (event) => {
        if (event.target === rail || !event.target.matches(':focus-visible')) return;
        const card = event.target.closest('.plp-rv__card');
        if (!card) return;
        const view = rail.getBoundingClientRect();
        const style = getComputedStyle(rail);
        const box = card.getBoundingClientRect();
        const inside = box.left >= view.left + parseFloat(style.paddingLeft) - 1 && box.right <= view.right - parseFloat(style.paddingRight) + 1;
        if (!inside) card.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'auto' });
        // whole sideways but cut by the fold or the sticky header: move the page only. The rail is asked for, not
        // the card, so no browser re-snaps the cards.
        else rail.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
      });
      rail.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-open]');
        if (opener) this.open(Number(opener.closest('.plp-rv__card').dataset.i));
      });
      for (const side of ['prev', 'next']) {
        this.$(side).addEventListener('click', (event) => {
          if (event.currentTarget.getAttribute('aria-disabled') !== 'true') this.move(side === 'next' ? 1 : -1, true);
        });
      }

      // the dimmed page closes the sheet, but only a press that also started there (dragging a text selection
      // out of the panel must not close it)
      const sheet = this.$('sheet');
      let pressedOutside = false;
      sheet.addEventListener('pointerdown', (event) => { pressedOutside = event.target === sheet; });
      sheet.addEventListener('click', (event) => { if (event.target === sheet && pressedOutside) sheet.close(); });
      sheet.querySelector('[data-sheet="close"]').addEventListener('click', () => sheet.close());

      this.measure();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.measure());
      if ('ResizeObserver' in window) {
        this.resizer = new ResizeObserver(() => this.measure());
        this.resizer.observe(rail);
      }
    }

    // One pass per card: how many lines of the review fit under its title, and whether anything is cut.
    // Without it the CSS still clamps the text; this only decides the line count and whether "Read more" shows.
    measure() {
      if (!this.items) return;
      const rail = this.$('rail');
      for (const item of this.items) {
        const quote = item.querySelector('.plp-rv__quote');
        const text = item.querySelector('.plp-rv__text');
        const title = item.querySelector('.plp-rv__title');
        const lineHeight = parseFloat(getComputedStyle(text).lineHeight) || 24;
        const room = quote.clientHeight - (title ? title.offsetHeight : 0);
        text.style.setProperty('-webkit-line-clamp', String(Math.max(1, Math.floor((room + 1) / lineHeight))));
        const card = this.cards[Number(item.dataset.i)];
        const cut = text.scrollHeight > text.clientHeight + 1 || (title && title.scrollHeight > title.clientHeight + 1);
        const more = item.querySelector('.plp-rv__more');
        // the sheet also holds what the card leaves off: a cut text, the customer's photo, a title not shown here
        more.hidden = !(cut || card.photo || (card.fullTitle && !card.title));
        more.firstElementChild.textContent = !cut && card.photo ? 'View photo' : 'Read more';
      }
      // nothing to move through (a few reviews on a wide screen): the arrows go and the rail is not a Tab stop
      const scrolls = rail.scrollWidth > rail.clientWidth + 2;
      this.$('nav').hidden = !scrolls;
      if (scrolls) rail.setAttribute('tabindex', '0'); else rail.removeAttribute('tabindex');
      this.sync();
    }

    sync() {
      const rail = this.$('rail');
      const end = rail.scrollWidth - rail.clientWidth;
      this.$('prev').setAttribute('aria-disabled', rail.scrollLeft < 4 ? 'true' : 'false');
      this.$('next').setAttribute('aria-disabled', rail.scrollLeft > end - 4 ? 'true' : 'false');
    }

    // the arrow keys move one card; the buttons move as many whole cards as fit
    move(direction, screenful) {
      const rail = this.$('rail');
      const [first, second] = this.items;
      if (!first) return;
      const stride = second ? second.offsetLeft - first.offsetLeft : rail.clientWidth;
      const style = getComputedStyle(rail);
      const view = rail.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const fit = screenful ? Math.max(1, Math.floor((view + stride - first.offsetWidth + 1) / stride)) : 1;
      // a position, not a movement: asked to move by a distance that lands a fraction past a snap point (card
      // widths are not whole pixels at some window widths), WebKit takes the NEXT snap point and skips a card
      // going back, count from the card boundary at or after where the rail is: under 1000px the rail's end is not a
      // card's start, and counting from it passed over a card on the way back
      const real = second ? second.getBoundingClientRect().left - first.getBoundingClientRect().left : stride;
      const from = direction < 0 ? Math.ceil((rail.scrollLeft - 2) / real) * real : rail.scrollLeft;
      rail.scrollTo({ left: from + direction * fit * stride, behavior: calm.matches ? 'auto' : 'smooth' });
    }

    open(index) {
      const card = this.cards[index];
      if (!card) return;
      const product = this.products[card.p];
      const sheet = this.$('sheet');
      if (typeof sheet.showModal !== 'function') {
        window.location.href = product.url;
        return;
      }
      const at = (name) => sheet.querySelector(`[data-sheet="${name}"]`);
      sheet.setAttribute('aria-label', `Review by ${card.name}`);
      fill(at('stars'), this.stars(card.rating));
      fill(at('who'),
        el('b', { text: card.name }),
        [when(card.date), card.verified ? this.dataset.verified : '', card.flagged ? (this.dataset.flag || 'Incentivized review') : '']
          .filter(Boolean).map((text) => el('span', { text })));
      const scroll = at('scroll');
      fill(scroll,
        card.fullTitle ? el('h3', { class: 'plp-rv__sheet-title', text: card.fullTitle }) : null,
        el('p', { class: 'plp-rv__sheet-text', text: card.full }),
        card.photo ? el('img', { class: 'plp-rv__sheet-photo', src: card.photo, alt: `Photo from ${card.name}`, loading: 'lazy' }) : null);
      fill(at('prod'), this.product(card, false));
      at('cta').href = product.url;
      sheet.showModal();
      scroll.scrollTop = 0;
      // a Tab stop only when there is something to scroll
      if (scroll.scrollHeight > scroll.clientHeight + 1) scroll.setAttribute('tabindex', '0'); else scroll.removeAttribute('tabindex');
    }

    // One box in place of the cards, as tall as the section was, so nothing under it moves.
    box(message, before, hadFocus) {
      const rail = this.$('rail');
      const link = this.querySelector('.plp-rv__foot [data-rv="all"]');
      const way = el('a', {
        class: 'plp-rv__link',
        href: link ? link.getAttribute('href') : '/pages/reviews',
        text: message ? 'Read them on the reviews page' : (link ? link.textContent : 'Read all reviews'),
      });
      const box = el('li', { class: 'plp-rv__fail' }, message ? el('b', { text: message }) : null, way);
      fill(rail, box);
      rail.classList.add('plp-rv__rail--one');
      rail.removeAttribute('tabindex');
      rail.setAttribute('aria-busy', 'false');
      this.$('nav').hidden = true;
      this.$('note').hidden = true;
      this.dataset.state = 'failed';
      const lost = (before || 0) - this.offsetHeight;
      if (lost > 0) box.style.minHeight = `${box.offsetHeight + lost}px`;
      if (hadFocus) way.focus({ preventScroll: true });
    }

    // the feed did not answer
    fail(before, hadFocus) {
      this.box('The reviews did not load.', before, hadFocus);
      this.$('status').textContent = 'The reviews did not load.';
    }

    // The feed answered and has nothing usable for these products. While the section is still below the screen
    // it steps aside (nothing in view moves); once it is in view it keeps its place and offers the way to every review.
    none(before, hadFocus) {
      if (this.getBoundingClientRect().top >= window.innerHeight) {
        (this.closest('.section') || this).style.display = 'none';
        this.dataset.state = 'empty';
        return;
      }
      if (this.dataset.score !== 'ready') this.drawScore(null);
      this.box('', before, hadFocus);
    }
  }

  customElements.define('plp-reviews', PlpReviews);
})();
